<template>
  <div class="chart-container">
    <h3 class="title is-6">Invoices by Store Code</h3>
    <div class="chart-wrapper">
      <canvas ref="departmentChart"></canvas>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
  name: 'InvoiceDepartmentChart',
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      chart: null
    }
  },
  mounted() {
    // Add a small delay to ensure data is available and DOM is stable
    setTimeout(() => {
      this.$nextTick(() => {
        this.createChart();
      });
    }, 100);
  },
  watch: {
    data: {
      deep: true,
      handler() {
        // Add delay to ensure data is stable before updating
        setTimeout(() => {
          this.$nextTick(() => {
            this.updateChart();
          });
        }, 50);
      }
    }
  },
  beforeUnmount() {
    this.destroyChart();
  },
  methods: {
    createChart() {
      // Always destroy existing chart first to prevent conflicts
      this.destroyChart();
      
      // Validate data first
      if (!this.data || !Array.isArray(this.data) || this.data.length === 0) {
        console.log('InvoiceDepartmentChart: No valid data available for chart creation');
        return;
      }

      if (!this.$refs.departmentChart) {
        console.log('InvoiceDepartmentChart: Canvas ref not available');
        return;
      }
      
      try {
        const canvas = this.$refs.departmentChart;
        
        // Ensure canvas is properly mounted in DOM
        if (!canvas.parentNode) {
          console.log('InvoiceDepartmentChart: Canvas not mounted in DOM');
          return;
        }
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.log('InvoiceDepartmentChart: Could not get canvas context');
          return;
        }
        
        const labels = this.data.map(item => item.department) || [];
        const values = this.data.map(item => item.count) || [];
        const maxValue = values.length > 0 ? Math.max(...values) : 10;
        
        // Validate we have actual data
        if (labels.length === 0 || values.length === 0) {
          console.log('InvoiceDepartmentChart: No labels or values available');
          return;
        }
        
        console.log('InvoiceDepartmentChart: Creating chart with data:', { labels, values });
        
        this.chart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: labels,
            datasets: [{
              label: 'Invoice Count',
              data: values,
              backgroundColor: '#0a3954',
              borderColor: '#082B3F',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false, // Disable all animations to prevent context conflicts
            plugins: {
              legend: {
                display: false
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  stepSize: Math.max(1, Math.ceil(maxValue / 10)) // Dynamic step size
                }
              },
              x: {
                ticks: {
                  maxRotation: 45
                }
              }
            }
          }
        });
        
        console.log('InvoiceDepartmentChart: Chart created successfully');
      } catch (error) {
        console.error('InvoiceDepartmentChart: Error creating chart:', error);
      }
    },
    updateChart() {
      if (!this.chart) {
        this.createChart();
        return;
      }
      
      try {
        const labels = this.data?.map(item => item.department) || [];
        const values = this.data?.map(item => item.count) || [];
        
        this.chart.data.labels = labels;
        this.chart.data.datasets[0].data = values;
        this.chart.update();
      } catch (error) {
        console.error('Error updating department chart:', error);
        this.destroyChart();
        this.$nextTick(() => {
          this.createChart();
        });
      }
    },
    destroyChart() {
      if (this.chart) {
        try {
          // Stop any ongoing animations before destroying
          this.chart.stop();
          this.chart.destroy();
          console.log('InvoiceDepartmentChart: Chart destroyed successfully');
        } catch (error) {
          console.error('InvoiceDepartmentChart: Error destroying chart:', error);
        } finally {
          this.chart = null;
        }
      }
    }
  }
}
</script>

<style scoped>
.chart-container {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.chart-wrapper {
  position: relative;
  height: 300px;
}
</style>
